import React, { useState } from 'react'
import { IoTrendingUpOutline, IoFootballOutline, IoWarningOutline } from 'react-icons/io5'
import { Widget } from '../Widget'
import { useCardsOverview, useDixonColesFixtures } from '../../../swr-config'
import type { PageId } from '../../BottomNav'

type HighlightType = 'cards' | 'goals'

interface HighlightItem {
  fixture_id: number
  home_team: string
  away_team: string
  prediction: string
  confidence: 'high' | 'medium' | 'low'
}

interface CardsGoalsHighlightsWidgetProps {
  isEditing?: boolean
  onRemove?: () => void
  onNavigate?: (page: PageId) => void
  maxItems?: number
}

export function CardsGoalsHighlightsWidget({
  isEditing,
  onRemove,
  onNavigate,
  maxItems = 5,
}: CardsGoalsHighlightsWidgetProps) {
  const [activeTab, setActiveTab] = useState<HighlightType>('cards')

  // Fetch cards overview sorted by confidence
  const { data: cardsData, error: cardsError, isLoading: cardsLoading } = useCardsOverview({
    sort: 'confidence',
    minConfidence: 0.6,
  })

  // Fetch goals data from Dixon-Coles endpoint (more accurate probabilities)
  const { data: dcData, error: dcError, isLoading: dcLoading } = useDixonColesFixtures({
    days_ahead: 3,
    sort_by: 'kickoff',
    sort_order: 'asc',
  })

  const isLoading = activeTab === 'cards' ? cardsLoading : dcLoading
  const error = activeTab === 'cards' ? cardsError : dcError

  // Transform cards data to display format
  const cardsHighlights: HighlightItem[] = (cardsData?.fixtures || [])
    .filter((f) => f.projected_total_cards && f.confidence_score)
    .slice(0, maxItems)
    .map((f) => ({
      fixture_id: f.fixture_id,
      home_team: f.home_team_name,
      away_team: f.away_team_name,
      prediction: `${f.projected_total_cards?.toFixed(1)} cards`,
      confidence: f.confidence_score && f.confidence_score >= 0.75 ? 'high' as const
        : f.confidence_score && f.confidence_score >= 0.5 ? 'medium' as const
        : 'low' as const,
    }))

  // Transform goals data from Dixon-Coles endpoint
  // D&C provides accurate BTTS and O2.5 probabilities
  const goalsHighlights: HighlightItem[] = (dcData?.fixtures || [])
    .filter((f) => f.probabilities && (f.probabilities.btts_yes || f.probabilities.over_2_5))
    .slice(0, maxItems)
    .map((f) => {
      const probs = f.probabilities!
      const bttsProb = probs.btts_yes || 0
      const o25Prob = probs.over_2_5 || 0
      // Show whichever market has higher probability
      const showBtts = bttsProb > o25Prob
      const mainProb = showBtts ? bttsProb : o25Prob

      return {
        fixture_id: f.fixture_id,
        home_team: f.home_team?.name || '',
        away_team: f.away_team?.name || '',
        prediction: showBtts
          ? `BTTS ${(bttsProb * 100).toFixed(0)}%`
          : `O2.5 ${(o25Prob * 100).toFixed(0)}%`,
        // High confidence if probability >= 60%, medium if >= 50%
        confidence: mainProb >= 0.6 ? 'high' as const
          : mainProb >= 0.5 ? 'medium' as const
          : 'low' as const,
      }
    })

  const highlights = activeTab === 'cards' ? cardsHighlights : goalsHighlights

  const handleHighlightClick = (fixtureId: number) => {
    window.history.pushState(null, '', `#match/${fixtureId}`)
    window.dispatchEvent(new HashChangeEvent('hashchange'))
  }

  const getConfidenceClass = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return 'confidence--high'
      case 'medium':
        return 'confidence--medium'
      default:
        return 'confidence--low'
    }
  }

  return (
    <Widget
      id="highlights"
      title="Cards & Goals"
      icon={<IoTrendingUpOutline size={18} />}
      isEditing={isEditing}
      onRemove={onRemove}
      onNavigate={onNavigate}
      isLoading={isLoading}
      error={error ? 'Failed to load highlights' : null}
      skeletonVariant="highlights"
      headerActions={
        <div className="widget-tab-switcher">
          <button
            className={`widget-tab ${activeTab === 'cards' ? 'widget-tab--active' : ''}`}
            onClick={() => setActiveTab('cards')}
          >
            <IoWarningOutline size={14} />
            Cards
          </button>
          <button
            className={`widget-tab ${activeTab === 'goals' ? 'widget-tab--active' : ''}`}
            onClick={() => setActiveTab('goals')}
          >
            <IoFootballOutline size={14} />
            Goals
          </button>
        </div>
      }
    >
      <div className="widget-highlights">
        {highlights.length === 0 ? (
          <div className="widget-empty">
            <p>No {activeTab} highlights available</p>
            <button
              className="widget-empty-action"
              onClick={() => onNavigate?.(activeTab === 'cards' ? 'cardshub' : 'goalshub')}
            >
              Open {activeTab === 'cards' ? 'Cards' : 'Goals'} Hub
            </button>
          </div>
        ) : (
          <ul className="widget-highlights-list">
            {highlights.map((highlight, index) => (
              <li
                key={`${highlight.fixture_id}-${index}`}
                className="widget-highlight-item"
                onClick={() => handleHighlightClick(highlight.fixture_id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleHighlightClick(highlight.fixture_id)
                  }
                }}
              >
                <div className="widget-highlight-match">
                  <span className="widget-highlight-teams">
                    {highlight.home_team} vs {highlight.away_team}
                  </span>
                </div>
                <div className="widget-highlight-details">
                  <span className="widget-highlight-prediction">
                    {highlight.prediction}
                  </span>
                  <span className={`widget-highlight-confidence ${getConfidenceClass(highlight.confidence)}`}>
                    {highlight.confidence}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Widget>
  )
}
